import { create } from 'zustand';
import { itemService } from '@/services/item-service';
import { vaultService } from '@/services/vault-service';
import { useCryptoStore } from './crypto-store';
import type { Item, DecryptedItem, ItemType, ItemData } from '@/types/item';

interface ItemFilters {
  vaultId?: string;
  folderId?: string;
  type?: ItemType;
  favorite?: boolean;
  deleted?: boolean;
}

interface CreateItemInput {
  vaultId: string;
  folderId?: string;
  type: ItemType;
  data: ItemData;
}

interface UpdateItemInput {
  folderId?: string | null;
  data?: ItemData;
  isFavorite?: boolean;
}

interface ItemState {
  items: DecryptedItem[];
  selectedItem: DecryptedItem | null;
  filters: ItemFilters;
  isLoading: boolean;

  fetchItems: (filters?: ItemFilters) => Promise<void>;
  getItem: (id: string) => Promise<DecryptedItem>;
  createItem: (input: CreateItemInput) => Promise<DecryptedItem>;
  updateItem: (id: string, input: UpdateItemInput) => Promise<DecryptedItem>;
  deleteItem: (id: string) => Promise<void>;
  restoreItem: (id: string) => Promise<void>;
  permanentDeleteItem: (id: string) => Promise<void>;
  setFilters: (filters: ItemFilters) => void;
  setSelectedItem: (item: DecryptedItem | null) => void;
  searchLocal: (term: string) => DecryptedItem[];
  getAvailableTags: () => string[];
  filterByTags: (tags: string[]) => DecryptedItem[];
}

export const useItemStore = create<ItemState>()((set, get) => ({
  items: [],
  selectedItem: null,
  filters: {},
  isLoading: false,

  fetchItems: async (filters) => {
    set({ isLoading: true });

    try {
      const mergedFilters = { ...get().filters, ...filters };
      const response = await itemService.getAll(mergedFilters);

      const crypto = useCryptoStore.getState();

      // Build vault key map for shared vaults
      const vaultKeyMap = new Map<string, Uint8Array>();
      try {
        const vaults = await vaultService.getAll(true);
        for (const vault of vaults) {
          if (vault.isOwner === false && vault.vaultKeyEncrypted) {
            try {
              const keyJson = await crypto.decrypt(vault.vaultKeyEncrypted);
              vaultKeyMap.set(vault.id, new Uint8Array(JSON.parse(keyJson)));
            } catch {
              // Can't decrypt this vault key, skip
            }
          }
        }
      } catch {
        // Failed to fetch vaults, continue with personal key only
      }

      const results = await Promise.all(
        response.data.map(async (item: Item) => {
          try {
            const vaultKey = vaultKeyMap.get(item.vaultId);
            const decryptedData = await crypto.decrypt(
              item.dataEncrypted,
              vaultKey
            );
            return {
              ...item,
              data: JSON.parse(decryptedData) as ItemData,
            } as DecryptedItem;
          } catch {
            return null;
          }
        })
      );

      set({
        items: results.filter((item): item is DecryptedItem => item !== null),
        filters: mergedFilters,
      });
    } finally {
      set({ isLoading: false });
    }
  },

  getItem: async (id) => {
    const response = await itemService.getOne(id);
    const crypto = useCryptoStore.getState();

    // Try with personal key first, then check shared vaults
    try {
      const decryptedData = await crypto.decrypt(response.dataEncrypted);
      return {
        ...response,
        data: JSON.parse(decryptedData) as ItemData,
      };
    } catch {
      // May be from a shared vault — get the vault key
      const vaults = await vaultService.getAll(true);
      const vault = vaults.find((v) => v.id === response.vaultId);
      if (vault?.vaultKeyEncrypted) {
        const keyJson = await crypto.decrypt(vault.vaultKeyEncrypted);
        const vaultKey = new Uint8Array(JSON.parse(keyJson));
        const decryptedData = await crypto.decrypt(
          response.dataEncrypted,
          vaultKey
        );
        return {
          ...response,
          data: JSON.parse(decryptedData) as ItemData,
        };
      }
      throw new Error('Cannot decrypt item');
    }
  },

  createItem: async (input) => {
    const crypto = useCryptoStore.getState();
    const encryptedData = await crypto.encrypt(JSON.stringify(input.data));

    const response = await itemService.create({
      vaultId: input.vaultId,
      folderId: input.folderId,
      type: input.type,
      dataEncrypted: encryptedData,
    });

    const newItem: DecryptedItem = {
      ...response,
      data: input.data,
    };

    set((state) => ({
      items: [newItem, ...state.items],
    }));

    return newItem;
  },

  updateItem: async (id, input) => {
    const crypto = useCryptoStore.getState();

    const updateData: Record<string, unknown> = {};

    if (input.data) {
      updateData.dataEncrypted = await crypto.encrypt(
        JSON.stringify(input.data)
      );
    }

    if (input.folderId !== undefined) {
      updateData.folderId = input.folderId;
    }

    if (input.isFavorite !== undefined) {
      updateData.isFavorite = input.isFavorite;
    }

    const response = await itemService.update(id, updateData);
    const decryptedData = await crypto.decrypt(response.dataEncrypted);

    const updatedItem: DecryptedItem = {
      ...response,
      data: JSON.parse(decryptedData) as ItemData,
    };

    set((state) => ({
      items: state.items.map((item) =>
        item.id === id ? updatedItem : item
      ),
      selectedItem:
        state.selectedItem?.id === id ? updatedItem : state.selectedItem,
    }));

    return updatedItem;
  },

  deleteItem: async (id) => {
    await itemService.delete(id);

    set((state) => ({
      items: state.items.filter((item) => item.id !== id),
      selectedItem:
        state.selectedItem?.id === id ? null : state.selectedItem,
    }));
  },

  restoreItem: async (id) => {
    await itemService.restore(id);
    await get().fetchItems();
  },

  permanentDeleteItem: async (id) => {
    await itemService.permanentDelete(id);

    set((state) => ({
      items: state.items.filter((item) => item.id !== id),
    }));
  },

  setFilters: (filters) => {
    set({ filters });
  },

  setSelectedItem: (item) => {
    set({ selectedItem: item });
  },

  searchLocal: (term: string) => {
    const { items } = get();
    const normalizedTerm = term.toLowerCase().trim();

    if (!normalizedTerm) {
      return items;
    }

    return items.filter((item) => {
      const data = item.data as unknown as Record<string, unknown>;

      // Include 'uri' for backwards compatibility with items imported before bug fix
      const searchableFields = [
        data.name,
        data.username,
        data.email,
        data.url,
        data.uri,
        data.notes,
      ].filter(Boolean);

      return searchableFields.some((field) =>
        String(field).toLowerCase().includes(normalizedTerm)
      );
    });
  },

  getAvailableTags: () => {
    const { items } = get();
    const tagSet = new Set<string>();

    items.forEach((item) => {
      if (item.data.tags) {
        item.data.tags.forEach((tag) => tagSet.add(tag));
      }
    });

    return Array.from(tagSet).sort();
  },

  filterByTags: (tags: string[]) => {
    const { items } = get();

    if (tags.length === 0) {
      return items;
    }

    return items.filter((item) => {
      if (!item.data.tags || item.data.tags.length === 0) {
        return false;
      }
      // Item deve ter pelo menos uma das tags selecionadas
      return tags.some((tag) => item.data.tags?.includes(tag));
    });
  },
}));
